import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import { SECTIONS } from './navConfig';

interface FlatItem {
  path: string;
  label: string;
  icon: (typeof SECTIONS)[number]['items'][number]['icon'];
  section?: string;
}

const ALL_ITEMS: FlatItem[] = SECTIONS.flatMap((section) =>
  section.items.map((item) => ({ ...item, section: section.title }))
);

export function TopNavSearch() {
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(0);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q === '') return [];
    return ALL_ITEMS.filter((item) => item.label.toLowerCase().includes(q)).slice(0, 8);
  }, [query]);

  useEffect(() => { setHighlighted(0); }, [query]);

  useEffect(() => {
    if (!open) return;

    function handlePointerDown(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [open]);

  const goTo = (item: FlatItem) => {
    navigate(item.path);
    setQuery('');
    setOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (results.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlighted((i) => (i + 1) % results.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlighted((i) => (i - 1 + results.length) % results.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      goTo(results[highlighted]);
    }
  };

  return (
    <div ref={containerRef} className="relative hidden max-w-xs flex-1 md:block">
      <div className="flex items-center gap-2 rounded-input border border-border bg-bg px-3 py-2 text-text-muted">
        <Search size={16} />
        <input
          type="text"
          placeholder="Search…"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          className="w-full bg-transparent text-[15px] text-text-primary placeholder:text-text-muted focus:outline-none"
        />
      </div>

      {open && query.trim() !== '' && (
        <div className="absolute left-0 top-full z-40 mt-2 w-[320px] max-h-[360px] overflow-y-auto rounded-card border border-border bg-surface shadow-gp-lg">
          {results.length === 0 ? (
            <div className="px-4 py-6 text-center text-[14px] text-text-secondary">
              No pages match &ldquo;{query}&rdquo;
            </div>
          ) : (
            results.map((item, index) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.path}
                  type="button"
                  onClick={() => goTo(item)}
                  onMouseEnter={() => setHighlighted(index)}
                  className={`flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                    index === highlighted ? 'bg-bg' : ''
                  } ${index !== results.length - 1 ? 'border-b border-border' : ''}`}
                >
                  <Icon size={16} className="shrink-0 text-text-secondary" />
                  <span className="flex min-w-0 flex-col">
                    <span className="truncate text-[14px] font-medium text-text-primary">{item.label}</span>
                    {item.section && <span className="text-[12px] text-text-muted">{item.section}</span>}
                  </span>
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
